import * as React from 'react';
import { Card } from '../primitives/card';
import { cn } from '../../lib/utils';

export interface SectionCardProps {
  title: React.ReactNode;
  /**
   * The right side of the header row. Either an action for the section, or a
   * note about how it behaves — "first image is the cover", "prices are set
   * per variation" — which belongs beside the heading rather than buried in
   * the body.
   */
  trailing?: React.ReactNode;
  children: React.ReactNode;
  /** Drops the body padding, for a section whose content is a full-bleed table. */
  flush?: boolean;
  /**
   * Heading inside the body with no rule under it, for the small cards in a
   * narrow column. A ruled header on a card four lines tall spends a third of
   * its height on chrome, and a column of them reads as a stack of tables
   * rather than as facts about the thing on the left.
   */
  quiet?: boolean;
  testId?: string;
  className?: string;
}

/**
 * A titled panel on a detail screen.
 *
 * Every section of the product editor was previously a bare card with an `h2`
 * floated inside its body, or no heading at all — so the page read as a stack
 * of boxes and you had to infer what each one was for. Naming the sections in
 * a consistent header row, with a rule under it, makes the page scannable and
 * gives the trailing note somewhere to live.
 */
export function SectionCard({
  title,
  trailing,
  children,
  flush = false,
  quiet = false,
  testId,
  className,
}: SectionCardProps) {
  return (
    <Card data-testid={testId} className={cn('overflow-hidden', className)}>
      <div
        className={cn(
          'flex items-center justify-between gap-4',
          quiet ? 'px-5 pb-2.5 pt-4' : 'border-b border-border px-5 py-3.5',
        )}
      >
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {trailing && (
          <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
            {trailing}
          </div>
        )}
      </div>
      <div className={cn(!flush && (quiet ? 'px-5 pb-5' : 'px-5 py-5'))}>{children}</div>
    </Card>
  );
}
